import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatContextProvider } from './context/ChatContext';
import { ToastProvider } from './context/ToastContext';
import PrivateRoute from './components/auth/PrivateRoute';

import Home from './pages/Home';
import HomeSignedIn from './pages/HomeSignedIn';
import Login from './components/auth/Login';



import BookReader from './pages/BookReader';
import Settings from './pages/Settings';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import HomeFooter from './components/common/HomeFooter';
import ScrollToTop from './components/common/ScrollToTop';
import Library from './pages/Library';
import ManuscriptStructure from './pages/ManuscriptStructure';
import ManuscriptTheme from './pages/ManuscriptTheme';
import TemplateGallery from './pages/TemplateGallery';
import CorkBoard from './pages/CorkBoard';
import DebugPanel from './components/common/DebugPanel';
import EditorHome from './pages/EditorHome';
import EditorPage from './pages/EditorPage';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import BookCover from './pages/BookCover';
import BookCoverDesigner from './components/BookCover/BookCoverDesigner';
import BlurbEditor from './components/editor/BlurbEditor';
import Communications from './pages/Communications';
import UsernameModal from './components/username/UsernameModal';
import ChatBubble from './components/common/ChatBubble';
import QuotePage from './pages/QuotePage';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogStudio from './pages/BlogStudio';
import Legal from './pages/Legal';
import Intake from './pages/Intake';
import IntakeSubmission from './pages/IntakeSubmission';
import PrintQueue from './pages/PrintQueue';
import Admin from './pages/Admin';
import ReviewQueue from './pages/ReviewQueue';

// Marketing pages that render their own masthead instead of the global <Navbar />.
const OWN_NAV_ROUTES = ["/", "/callback", "/logout", "/pricing", "/quote", "/about", "/community", "/legal", "/terms", "/privacy"];

// Marketing pages share the lighter HomeFooter; everything else gets the app <Footer />.
const MARKETING_FOOTER_ROUTES = ["/", "/callback", "/logout", "/pricing", "/quote", "/about", "/community", "/legal", "/terms", "/privacy"];

// Public pages that switch to the app's <Navbar /> and <Footer /> for a signed-in
// user. The dashboard Sidebar links to /community, and the marketing masthead
// offered no way back into the app. Signed out, these stay marketing pages.
const APP_CHROME_WHEN_SIGNED_IN = ["/community"];

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const appChrome = Boolean(user) && APP_CHROME_WHEN_SIGNED_IN.includes(location.pathname);
  const ownNav = OWN_NAV_ROUTES.includes(location.pathname) && !appChrome;
  const marketingFooter = MARKETING_FOOTER_ROUTES.includes(location.pathname) && !appChrome;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Resets scroll on route change — without it you land mid-page after navigating
          from the bottom of a long one. Renders nothing. */}
      <ScrollToTop />
     {!ownNav && <Navbar />}
      {process.env.NODE_ENV === 'development' && <DebugPanel />}

      <Box component="main" sx={{ flexGrow: 1 }}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/callback" element={<Home />} />
                  <Route path="/logout" element={<Home />} />
                  <Route path="/quote" element={<QuotePage />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/community" element={<Blog />} />
                  {/* One document, three entry points: /legal opens at the top, /terms and
                      /privacy land on their half of it. See the header comment in Legal.jsx. */}
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/terms" element={<Legal />} />
                  <Route path="/privacy" element={<Legal />} />

                  {/* Protected routes */}
                  {/* Signed-in landing page. "/" stays the marketing page for visitors;
                      Navbar's Home button points here once there's a user. Deliberately
                      kept out of OWN_NAV_ROUTES / MARKETING_FOOTER_ROUTES — it takes the
                      global Navbar and the app Footer, same as /dashboard. */}
                  <Route path="/home" element={<PrivateRoute><HomeSignedIn /></PrivateRoute>} />
                  <Route path="/chat" element={<PrivateRoute><Communications /></PrivateRoute>} />
                  <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                  <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
                  <Route path="/review-queue" element={<PrivateRoute><ReviewQueue /></PrivateRoute>} />
                  <Route path="/print-queue" element={<PrivateRoute><PrintQueue /></PrivateRoute>} />
                  <Route path="/intake" element={<PrivateRoute><Intake /></PrivateRoute>} />
                  <Route path="/intake/:reference" element={<PrivateRoute><IntakeSubmission /></PrivateRoute>} />
                  <Route path="/library" element={<PrivateRoute><Library /></PrivateRoute>} />
                  <Route path="/templates" element={<PrivateRoute><TemplateGallery /></PrivateRoute>} />
                  <Route path="/reader/:bookId" element={<PrivateRoute><BookReader /></PrivateRoute>} />
                  <Route path="/editor-home" element={<PrivateRoute><EditorHome /></PrivateRoute>} />
                  <Route path="/editor/:id" element={<PrivateRoute><EditorPage /></PrivateRoute>} />
                  <Route path="/manuscript/:id/structure" element={<PrivateRoute><ManuscriptStructure /></PrivateRoute>} />
                  {/* The Theme Editor for one manuscript. Keeps the global Navbar: not in OWN_NAV_ROUTES. */}
                  <Route path="/manuscript/:id/theme" element={<PrivateRoute><ManuscriptTheme /></PrivateRoute>} />
                  <Route path="/manuscript/:id/cork-board" element={<PrivateRoute><CorkBoard /></PrivateRoute>} />
                  {/* The /quote estimator, opened on one manuscript from its Library menu.
                      Its own path rather than /quote plus APP_CHROME_WHEN_SIGNED_IN: it is
                      never a marketing page, so it takes the app chrome by default, and
                      signed-in visits to the public /quote stay as they are. */}
                  <Route path="/manuscript/:id/pricing" element={<PrivateRoute><QuotePage /></PrivateRoute>} />
                  <Route path="/editor/:id/cover" element={<BookCover />} />
                  {/* The same page as /manuscript/:id/structure. A standalone cover
                      is a draft like any other, so one component serves both; the URL
                      matches the cover family because that is where the author came
                      from. The page decides what to show from the draft's `type`. */}
                  <Route path="/bookcover/:id/structure" element={<PrivateRoute><ManuscriptStructure /></PrivateRoute>} />
                  <Route path="/bookcover/:id/designer" element={<BookCoverDesigner />} />
                  <Route path="/bookcover/:id/designer/:versionId" element={<BookCoverDesigner />} />
                  <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                  <Route path="/read/:bookId"   element={<PrivateRoute><BookReader /></PrivateRoute>} />
                  <Route path="/review/:bookId" element={<PrivateRoute><BookReader /></PrivateRoute>} />
                  <Route path="/editor/:id/blurb" element={<BlurbEditor />} />
                  <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
                  {/* Authoring surface for the Community blog. Signed-in app shell, not the
                      marketing one — the role gate that matters is on the endpoints. */}
                  <Route path="/blog-studio" element={<PrivateRoute><BlogStudio /></PrivateRoute>} />

                  {/* Fallback */}
                  <Route path="*" element={<h2>Page not found</h2>} />
                </Routes>

      </Box>

      {marketingFooter ? <HomeFooter /> : <Footer />}
      <ChatBubble />
    </Box>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ChatContextProvider>
          <ToastProvider>
            <AppContent />
            <UsernameModal />
          </ToastProvider>
        </ChatContextProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
